import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../api'

interface PortfolioReslotMove {
  supply_id: number
  from_date: string
  to_date: string
  days_earlier: number
}

interface PortfolioReslotCostBreakdown {
  baseline?: { total_rub?: number; logistics_rub?: number; oos_penalty_rub?: number; early_bonus_rub?: number }
  proposed?: { total_rub?: number; logistics_rub?: number; oos_penalty_rub?: number; early_bonus_rub?: number }
}

interface PortfolioReslotProposal {
  id: number
  proposed_at: string | null
  expires_at: string | null
  state: string
  savings_rub: number
  baseline_cost_rub: number
  proposed_cost_rub: number
  moves: PortfolioReslotMove[]
  cost_breakdown: PortfolioReslotCostBreakdown
  applied_at: string | null
  applied_moves?: unknown[]
  failed_moves?: unknown[]
}

interface ProposalsResponse {
  status: string
  proposals: PortfolioReslotProposal[]
}

const ROUBLE_FORMATTER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })

function formatRub(value: number): string {
  return `${ROUBLE_FORMATTER.format(Math.round(value))} ₽`
}

function formatDate(value: string): string {
  // ISO date 2026-05-08 → 08.05
  if (!value || value.length < 10) return value
  return `${value.slice(8, 10)}.${value.slice(5, 7)}`
}

function formatExpiresAt(iso: string | null): string {
  if (!iso) return ''
  const expires = new Date(iso)
  if (Number.isNaN(expires.getTime())) return ''
  const remainingMs = expires.getTime() - Date.now()
  if (remainingMs <= 0) return 'истекло'
  const minutes = Math.max(1, Math.round(remainingMs / 60000))
  return `действительно ещё ${minutes} мин`
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Не удалось выполнить запрос'
}

interface PortfolioReslotBannerProps {
  /** Polling interval in ms. Defaults to 60s. */
  pollIntervalMs?: number
}

export function PortfolioReslotBanner({ pollIntervalMs = 60_000 }: PortfolioReslotBannerProps) {
  const [proposals, setProposals] = useState<PortfolioReslotProposal[]>([])
  const [expanded, setExpanded] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const response = await apiFetch('/ai/portfolio-reslot/proposals?state=pending_approval')
      const body: ProposalsResponse = await response.json()
      const next = Array.isArray(body?.proposals) ? body.proposals : []
      setProposals(next)
      setErrorMessage(null)
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(error))
    }
  }, [])

  useEffect(() => {
    load().catch(() => undefined)
    if (pollIntervalMs <= 0) return undefined
    const handle = window.setInterval(() => {
      load().catch(() => undefined)
    }, pollIntervalMs)
    return () => window.clearInterval(handle)
  }, [load, pollIntervalMs])

  const totalSavings = useMemo(
    () => proposals.reduce((sum, proposal) => sum + (proposal.savings_rub || 0), 0),
    [proposals],
  )
  const totalMoves = useMemo(
    () => proposals.reduce((sum, proposal) => sum + (proposal.moves?.length || 0), 0),
    [proposals],
  )

  const handleApply = useCallback(
    async (proposalId: number) => {
      setBusyId(proposalId)
      setNoticeMessage(null)
      try {
        const response = await apiFetch(`/ai/portfolio-reslot/apply/${proposalId}`, { method: 'POST' })
        if (!response.ok) {
          throw new Error(`Apply failed: ${response.status}`)
        }
        setNoticeMessage(`Заявка на применение #${proposalId} поставлена в очередь`)
        await load()
      } catch (error: unknown) {
        setErrorMessage(getErrorMessage(error))
      } finally {
        setBusyId(null)
      }
    },
    [load],
  )

  const handleDismiss = useCallback(
    async (proposalId: number) => {
      setBusyId(proposalId)
      setNoticeMessage(null)
      try {
        const response = await apiFetch(`/ai/portfolio-reslot/dismiss/${proposalId}`, { method: 'POST' })
        if (!response.ok) {
          throw new Error(`Dismiss failed: ${response.status}`)
        }
        setNoticeMessage(`Предложение #${proposalId} отклонено`)
        await load()
      } catch (error: unknown) {
        setErrorMessage(getErrorMessage(error))
      } finally {
        setBusyId(null)
      }
    },
    [load],
  )

  if (proposals.length === 0) return null

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold">
            Оптимизация поставок — можно сэкономить {formatRub(totalSavings)}
          </div>
          <div className="text-xs text-emerald-800">
            {proposals.length} предложений, всего {totalMoves} переносов
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(value => !value)}
          className="rounded-xl border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-900 transition hover:bg-emerald-100"
        >
          {expanded ? 'Свернуть' : 'Подробнее'}
        </button>
      </div>

      {errorMessage && (
        <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {errorMessage}
        </div>
      )}
      {noticeMessage && (
        <div className="mt-2 rounded-xl border border-emerald-300 bg-white px-3 py-2 text-xs text-emerald-800">
          {noticeMessage}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-3">
          {proposals.map(proposal => {
            const baseline = proposal.cost_breakdown?.baseline?.total_rub ?? proposal.baseline_cost_rub
            const proposed = proposal.cost_breakdown?.proposed?.total_rub ?? proposal.proposed_cost_rub
            const expiresLabel = formatExpiresAt(proposal.expires_at)
            return (
              <div
                key={proposal.id}
                className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs text-slate-800"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">
                      Предложение #{proposal.id} — экономия {formatRub(proposal.savings_rub)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {formatRub(baseline)} → {formatRub(proposed)}
                      {expiresLabel ? ` • ${expiresLabel}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleDismiss(proposal.id)}
                      disabled={busyId === proposal.id}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply(proposal.id)}
                      disabled={busyId === proposal.id}
                      className="rounded-lg border border-emerald-500 bg-emerald-600 px-3 py-1 text-[11px] font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {busyId === proposal.id ? '...' : 'Применить'}
                    </button>
                  </div>
                </div>
                {proposal.moves?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[11px] text-slate-700">
                    {proposal.moves.map(move => (
                      <li key={`${proposal.id}:${move.supply_id}`}>
                        Поставка #{move.supply_id}: {formatDate(move.from_date)} → {formatDate(move.to_date)} (раньше на {move.days_earlier} дн.)
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PortfolioReslotBanner
